import type { ApiClient } from '@nly/api-client'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { apiClient } from '@/api'
import { formatApiError } from '@/utils/apiError'

type PatientListResult = Awaited<ReturnType<ApiClient['patient']['list']>>
export type Patient = PatientListResult['items'][number]
export type PatientProfileInput = Parameters<ApiClient['patient']['create']>[0]

export const usePatientStore = defineStore('patient', () => {
  const listResult = ref<PatientListResult | null>(null)
  const currentPatient = ref<Awaited<ReturnType<ApiClient['patient']['get']>> | null>(null)
  const listLoading = ref(false)
  const detailLoading = ref(false)
  const mutationLoading = ref(false)
  const listError = ref<string | null>(null)
  const detailError = ref<string | null>(null)
  let listGeneration = 0

  const items = computed(() => listResult.value?.items ?? [])
  const total = computed(() => listResult.value?.total ?? 0)

  async function loadList(input: Parameters<typeof apiClient.patient.list>[0]) {
    const request = ++listGeneration
    listLoading.value = true
    listError.value = null
    try {
      const result = await apiClient.patient.list(input)
      if (request !== listGeneration) {
        return result
      }
      listResult.value = result
      return result
    }
    catch (error) {
      if (request === listGeneration) {
        listError.value = formatApiError(error, '患者列表加载失败')
      }
      throw error
    }
    finally {
      if (request === listGeneration) {
        listLoading.value = false
      }
    }
  }

  async function loadDetail(id: number) {
    const request = ++listGeneration
    detailLoading.value = true
    detailError.value = null
    try {
      const result = await apiClient.patient.get({ id })
      if (request !== listGeneration) {
        return result
      }
      currentPatient.value = result
      return result
    }
    catch (error) {
      if (request === listGeneration) {
        detailError.value = formatApiError(error, '患者详情加载失败')
      }
      throw error
    }
    finally {
      if (request === listGeneration) {
        detailLoading.value = false
      }
    }
  }

  async function create(input: PatientProfileInput) {
    mutationLoading.value = true
    try {
      return await apiClient.patient.create(input)
    }
    finally {
      mutationLoading.value = false
    }
  }

  async function update(id: number, profile: PatientProfileInput) {
    mutationLoading.value = true
    try {
      const result = await apiClient.patient.update({ id, profile })
      currentPatient.value = result
      return result
    }
    finally {
      mutationLoading.value = false
    }
  }

  function reset() {
    listGeneration++
    listResult.value = null
    currentPatient.value = null
    listLoading.value = false
    detailLoading.value = false
    mutationLoading.value = false
    listError.value = null
    detailError.value = null
  }

  return {
    items,
    total,
    listResult,
    currentPatient,
    listLoading,
    detailLoading,
    mutationLoading,
    listError,
    detailError,
    loadList,
    loadDetail,
    create,
    update,
    reset,
  }
})
